import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Layers,
  LogOut,
  Sun,
  Moon,
  BarChart2,
} from 'lucide-react';
import aiStarLogo from '../assets/img/ai-star.png';
import { useAuth } from '../contexts/AuthContext';
import HomePage from './HomePage';
import DocumentCanvas from './DocumentCanvas';
import ProgressDashboard from './ProgressDashboard';
import SourcesPanel from './SourcesPanel';
import StudioPanel from './StudioPanel';
import { useSources } from '../context/SourcesContext';
import styles from '../styles/appShell.module.css';
import { recordStudySession } from './ProgressDashboard';
import { useTheme } from '../context/ThemeContext';

const EXTRA_VIEWS = [
  { id: 'workspace', label: 'Canvas', Icon: Layers },
  { id: 'progress',  label: 'Progress', Icon: BarChart2 },
];

const AppShell = () => {
  const [extraView, setExtraView] = useState(null); // null = chat is main; 'workspace' | 'progress'
  const [sourcesCollapsed, setSourcesCollapsed] = useState(false);
  const [studioCollapsed, setStudioCollapsed] = useState(false);

  const shellRef = useRef(null);
  const { isDarkMode, toggleTheme } = useTheme();
  const { signOut, user } = useAuth();
  const { getSourcesContext } = useSources();

  const firstName =
    user?.user_metadata?.full_name?.split(' ')[0] ||
    user?.user_metadata?.name?.split(' ')[0] ||
    user?.email?.split('@')[0] ||
    null;

  // Cross-view bridges
  const [pendingLumiMsg, setPendingLumiMsg] = useState(null);
  const [pendingChatAppend, setPendingChatAppend] = useState(null);
  const [pendingKgText, setPendingKgText] = useState(null);
  const [pendingKgTitle, setPendingKgTitle] = useState(null);

  useEffect(() => {
    if (extraView) recordStudySession('session', extraView);
  }, [extraView]);

  const handleOpenLumiWithMessage = useCallback((text) => {
    setPendingLumiMsg(text);
    setExtraView(null);
  }, []);

  const handleQueueLumiDraft = useCallback((text) => {
    setPendingChatAppend(text);
    setExtraView(null);
    toast.message('Queued for Lumi', {
      description: 'Review, edit, and send from the chat panel.',
    });
  }, []);

  const handleNotebookKnowledgeGraph = useCallback((text, title) => {
    setPendingKgText(text);
    setPendingKgTitle(title);
    setExtraView('workspace');
  }, []);

  // Source-aware context for every chat message
  const getExtraContext = useCallback(() => getSourcesContext(), [getSourcesContext]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault();
        setSourcesCollapsed((v) => !v);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setStudioCollapsed((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const renderCenterView = () => {
    if (extraView === 'workspace') {
      return (
        <DocumentCanvas
          panelMode
          onClose={() => setExtraView(null)}
          onAskLumi={handleOpenLumiWithMessage}
          pendingKgText={pendingKgText}
          pendingKgTitle={pendingKgTitle}
          onKgConsumed={() => { setPendingKgText(null); setPendingKgTitle(null); }}
        />
      );
    }
    if (extraView === 'progress') {
      return <ProgressDashboard />;
    }
    return (
      <HomePage
        shellEmbedded
        pendingMessage={pendingLumiMsg}
        onPendingMessageConsumed={() => setPendingLumiMsg(null)}
        pendingChatAppend={pendingChatAppend}
        onPendingChatAppendConsumed={() => setPendingChatAppend(null)}
        getExtraContext={getExtraContext}
      />
    );
  };

  return (
    <div className={styles.shell} ref={shellRef}>
      {/* ── Top Navigation Bar ─────────────────────────────────────── */}
      <header className={styles.topBar}>
        <div className={styles.brand}>
          <div className={styles.brandMark}>
            <img src={aiStarLogo} alt="Lumi" width={20} height={20} style={{ display: 'block' }} />
          </div>
          <span className={styles.brandName}>Bramlo</span>
        </div>

        <nav className={styles.tabNav} aria-label="Extra views">
          {EXTRA_VIEWS.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`${styles.tab} ${extraView === id ? styles.tabActive : ''}`}
              onClick={() => setExtraView(extraView === id ? null : id)}
              aria-pressed={extraView === id}
            >
              <Icon size={14} strokeWidth={1.8} />
              <span>{label}</span>
            </button>
          ))}
        </nav>

        <div className={styles.topBarRight}>
          {firstName && (
            <span className={styles.userChip}>
              <span className={styles.userAvatar}>{firstName[0].toUpperCase()}</span>
              <span className={styles.userName}>{firstName}</span>
            </span>
          )}
          <button className={styles.iconBtn} onClick={toggleTheme} title="Toggle theme">
            {isDarkMode ? <Sun size={15} strokeWidth={1.75} /> : <Moon size={15} strokeWidth={1.75} />}
          </button>
          <button className={styles.iconBtn} onClick={signOut} title="Sign out">
            <LogOut size={15} strokeWidth={1.75} />
          </button>
        </div>
      </header>

      {/* ── 3-panel body: Sources | Chat | Studio ──────────────────── */}
      <div className={styles.workspace}>
        {/* Left: Sources */}
        <SourcesPanel
          collapsed={sourcesCollapsed}
          onToggleCollapse={() => setSourcesCollapsed((v) => !v)}
        />

        {/* Center: Chat / Canvas / Progress */}
        <main className={styles.centerPane}>
          <AnimatePresence mode="wait">
            <motion.div
              key={extraView || 'chat'}
              className={styles.centerInner}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              {renderCenterView()}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Right: Studio */}
        <StudioPanel
          collapsed={studioCollapsed}
          onToggleCollapse={() => setStudioCollapsed((v) => !v)}
          onSendToChat={handleOpenLumiWithMessage}
          onGenerateKnowledgeGraph={handleNotebookKnowledgeGraph}
          onOpenLumiWithMessage={handleOpenLumiWithMessage}
          onQueueLumiDraft={handleQueueLumiDraft}
        />
      </div>
    </div>
  );
};

export default AppShell;
